/**
 * Local settings + offline answer queue.
 *
 * The queue is the reason this works while travelling: answers are written to
 * localStorage immediately and flushed to GitHub when connectivity returns.
 */

import type { RepoConfig } from "./github";

const CONFIG_KEY = "psychotp.console.config";
const QUEUE_KEY = "psychotp.console.queue";

export interface PendingWrite {
  id: string;
  path: string;
  content: string;
  message: string;
  /** sha we based the write on, if known. */
  sha?: string;
  createdAt: number;
}

export function loadConfig(): RepoConfig | null {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (!raw) return null;
    const cfg = JSON.parse(raw) as RepoConfig;
    if (!cfg.owner || !cfg.repo || !cfg.token) return null;
    return cfg;
  } catch {
    return null;
  }
}

export function saveConfig(cfg: RepoConfig): void {
  localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
}

export function clearConfig(): void {
  localStorage.removeItem(CONFIG_KEY);
}

export function loadQueue(): PendingWrite[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? (JSON.parse(raw) as PendingWrite[]) : [];
  } catch {
    return [];
  }
}

export function saveQueue(queue: PendingWrite[]): void {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

export function enqueue(write: Omit<PendingWrite, "id" | "createdAt">): PendingWrite {
  const entry: PendingWrite = {
    ...write,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: Date.now(),
  };
  const queue = loadQueue();
  // Collapse repeated writes to the same file: keep only the newest.
  const filtered = queue.filter((q) => q.path !== write.path);
  filtered.push(entry);
  saveQueue(filtered);
  return entry;
}

export function dequeue(id: string): void {
  saveQueue(loadQueue().filter((q) => q.id !== id));
}
